import { useSyncExternalStore } from 'react';

import { DIMENSIONS } from './mock';
import {
  USE_API,
  archiveCaseApi,
  createCaseApi,
  createClientApi,
  deleteCaseApi,
  fetchActiveQuestionBankVersionId,
  fetchCases,
  fetchClients
} from './client';
import { caseSummaryToRecord, toCreateCaseRequest } from './adapters';
import { getSession } from './session';
import type { Dimension } from './types';

export type CaseLanguage = 'es' | 'en' | 'pt';

export const CASE_STATES = [
  'Draft',
  'In Progress',
  'Under Review',
  'Under Approval',
  'Approved',
  'Rejected',
  'Cancelled'
] as const;

export interface StateChange {
  state: string;
  at: string;
}

export interface CaseRecord {
  caseId: string;
  name: string;
  client: string;
  country: string;
  language: CaseLanguage;
  state: string;
  /** Dimension ids the facilitator has enabled for this case. */
  enabledDimensions: string[];
  /** Ordered history of state transitions with timestamps. */
  stateHistory: StateChange[];
  createdAt: string;
  updatedAt: string;
  /** Admin-archived cases are hidden from the default case list. */
  archived?: boolean;
}

const KEY = 'finops.cases';
const listeners = new Set<() => void>();

function seed(): CaseRecord[] {
  return [
    {
      caseId: 'CASE-20260810-000001',
      name: 'Banco Andino 2026',
      client: 'Banco Andino',
      country: 'Chile',
      language: 'es',
      state: 'In Progress',
      enabledDimensions: ['understand', 'quantify'],
      stateHistory: [
        { state: 'Draft', at: '2026-08-10 09:00' },
        { state: 'In Progress', at: '2026-08-12 14:30' }
      ],
      createdAt: '2026-08-10',
      updatedAt: '2026-08-13'
    },
    {
      caseId: 'CASE-20260811-000002',
      name: 'Retail Global FinOps',
      client: 'Retail Global',
      country: 'México',
      language: 'es',
      state: 'Draft',
      enabledDimensions: ['understand'],
      stateHistory: [{ state: 'Draft', at: '2026-08-11 10:00' }],
      createdAt: '2026-08-11',
      updatedAt: '2026-08-11'
    }
  ];
}

function read(): CaseRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as CaseRecord[];
  } catch {
    /* fall through to seed */
  }
  const seeded = seed();
  localStorage.setItem(KEY, JSON.stringify(seeded));
  return seeded;
}

let cache: CaseRecord[] = read();

function commit(next: CaseRecord[]): void {
  cache = next;
  localStorage.setItem(KEY, JSON.stringify(next));
  listeners.forEach((l) => l());
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function todayCompact(): string {
  return today().replace(/-/g, '');
}

function now(): string {
  return new Date().toISOString().slice(0, 16).replace('T', ' ');
}

function nextMockCaseId(): string {
  const nextSequence = cache.reduce((max, entry) => {
    const match = /^CASE-\d{8}-(\d{6})$/.exec(entry.caseId);
    return Math.max(max, match ? Number(match[1]) : 0);
  }, 0) + 1;

  return `CASE-${todayCompact()}-${String(nextSequence).padStart(6, '0')}`;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** In API mode, replaces the cache with cases fetched from case-service. */
export async function loadCases(): Promise<void> {
  if (!USE_API) return;
  const [cases, clients] = await Promise.all([fetchCases(), fetchClients()]);
  const clientsById = new Map(clients.map((c) => [c.id, c]));
  commit(cases.map((c) => caseSummaryToRecord(c, clientsById)));
}

/** Creates a client org via the API and returns its id. */
export async function createClientViaApi(input: {
  name: string;
  country: string;
  industry: string;
}): Promise<string> {
  const created = await createClientApi({ ...input, status: 'ACTIVE' });
  return created.id;
}

/** Creates a case via the API (resolving version + facilitator) and reloads the list. */
export async function createCaseViaApi(
  input: { name: string; language: CaseLanguage; country?: string; industry?: string },
  clientId: string
): Promise<CaseRecord> {
  const versionId = await fetchActiveQuestionBankVersionId();
  const userId = getSession()?.userId;
  if (!versionId) throw new Error('No published question bank version');
  if (!userId) throw new Error('No session');
  // Cases start with no dimensions enabled (locked by default); the facilitator
  // enables them in Config (case-service v0.1.5 accepts an empty array).
  const created = await createCaseApi(
    toCreateCaseRequest(input, clientId, versionId, [userId], [])
  );
  await loadCases();
  return cache.find((c) => c.caseId === created.caseId) ?? cache[0];
}

/** Archives/unarchives a case via the API (admin-only) and reloads the list. */
export async function archiveCaseViaApi(caseId: string, archived: boolean): Promise<void> {
  if (USE_API) {
    await archiveCaseApi(caseId, archived);
    await loadCases();
    return;
  }
  commit(cache.map((c) => (c.caseId === caseId ? { ...c, archived } : c)));
}

/**
 * Permanently deletes a case (admin-only). Returns false when the backend
 * rejects the password / privilege (401/403); the caller keeps the modal open.
 */
export async function deleteCaseViaApi(caseId: string, password: string): Promise<boolean> {
  if (USE_API) {
    const { ok, unauthorized } = await deleteCaseApi(caseId, password);
    if (unauthorized) return false;
    if (!ok) throw new Error('delete failed');
    await loadCases();
    return true;
  }
  commit(cache.filter((c) => c.caseId !== caseId));
  return true;
}

export function getCases(): CaseRecord[] {
  return cache;
}

export function getCase(id: string | undefined): CaseRecord | undefined {
  return id ? cache.find((c) => c.caseId === id) : undefined;
}

export function createCase(input: {
  name: string;
  client: string;
  country: string;
  language: CaseLanguage;
}): CaseRecord {
  const record: CaseRecord = {
    caseId: nextMockCaseId(),
    ...input,
    state: 'Draft',
    enabledDimensions: [],
    stateHistory: [{ state: 'Draft', at: now() }],
    createdAt: today(),
    updatedAt: today()
  };
  commit([record, ...cache]);
  return record;
}

export function updateCase(id: string, patch: Partial<CaseRecord>): void {
  commit(cache.map((c) => (c.caseId === id ? { ...c, ...patch, updatedAt: today() } : c)));
}

/** Changes the case state and records the transition in its history. */
export function updateCaseState(id: string, state: string): void {
  commit(
    cache.map((c) =>
      c.caseId === id && c.state !== state
        ? {
            ...c,
            state,
            stateHistory: [...c.stateHistory, { state, at: now() }],
            updatedAt: today()
          }
        : c
    )
  );
}

/** Dimension objects enabled for a case, in the canonical DIMENSIONS order. */
export function enabledDimensionsOf(record: CaseRecord | undefined): Dimension[] {
  if (!record) return [];
  return DIMENSIONS.filter((d) => record.enabledDimensions.includes(d.id));
}

export function useCases(): CaseRecord[] {
  return useSyncExternalStore(subscribe, getCases);
}

export function useCase(id: string | undefined): CaseRecord | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (id ? cache.find((c) => c.caseId === id) : undefined)
  );
}
